import React, { ReactNode, useMemo, useEffect } from 'react'
import {
  inspectorHoveredElementContext,
  inspectorContext,
  useInspectorOverlayEvents,
  InspectorContext,
  useHasAnyLayerWithExportableAssets,
  findSketchElementFromUuid,
} from '../../hooks/inspector'
import { useGetActiveSidebarTab } from '../../../components/ShareSidebarTabContext'
import { useSketchSceneTree } from '../../hooks/inspector/useSketchSceneTree'
import { useCanvasInteractions } from '../../../../canvas/components/CanvasInteractions'
import { useSelectedFrame } from '../../../../canvas/components/SelectedFrameContext'
import { isTopLevelFrameElement } from '../../../../inspector'
import { InspectCommandBarRegistration } from '../../../commandBarCommands/InspectCommandBarRegistration'

type FrameInspectorProviderProps = {
  children: ReactNode
  /**
   * Automatically select the top-level frame element when the inspector is active.
   * This can only work if the root is a top-level frame element (frame view).
   */
  autoSelectRootInitially: boolean
  /**
   * When set, the element with this UUID is used as the scene root for the inspector
   * instead of the actual scene root. This allows scoping the inspector layer tree
   * to a specific frame without switching the canvas to frame-only mode.
   */
  rootElementUUID?: string
}

export function FrameInspectorProvider({
  children,
  autoSelectRootInitially,
  rootElementUUID,
}: FrameInspectorProviderProps) {
  const activeSidebarTab = useGetActiveSidebarTab()
  const isInspectorActive = activeSidebarTab === 'Inspect'

  const { sketchRootElement, hasFailed, isTreeFullyLoaded } =
    useSketchSceneTree(isInspectorActive)

  // When rootElementUUID is set, scope the inspector to that element's subtree.
  // This lets the layer tree show only the children of a specific frame
  // without switching the canvas to frame-only mode.
  const inspectorRootElement = useMemo(() => {
    if (!rootElementUUID || !sketchRootElement) return sketchRootElement
    return (
      findSketchElementFromUuid(sketchRootElement, rootElementUUID) ??
      sketchRootElement
    )
  }, [rootElementUUID, sketchRootElement])

  const hasAnyLayerWithExportableAssets =
    useHasAnyLayerWithExportableAssets(inspectorRootElement)

  const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

  // Enable inspector interactions only when inspector is active and canvas interactions are enabled
  const enableInspectorInteractions =
    isInspectorActive && isCanvasInteractionsEnabled

  const {
    selectedElement,
    hoveredElement,
    setSelectedElement,
    setHoveredElement,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
  } = useInspectorOverlayEvents(
    inspectorRootElement,
    enableInspectorInteractions,
    autoSelectRootInitially,
    isTreeFullyLoaded
  )

  let inspectorStatus: InspectorContext['inspectorStatus'] = 'pending'

  if (inspectorRootElement) {
    inspectorStatus = 'ready' as const
  } else if (hasFailed) {
    inspectorStatus = 'errored' as const
  }

  // Sync selected top-level frame to SelectedFrameContext so copy link shortcut
  // can use a single source of truth for the selected frame
  const selectedFrameContext = useSelectedFrame()
  useEffect(() => {
    if (!selectedFrameContext) return

    if (selectedElement && isTopLevelFrameElement(selectedElement)) {
      selectedFrameContext.setSelectedFrameUUID(selectedElement.elementUUID)
    } else if (isInspectorActive) {
      // Clear the selection when in inspector mode but no top-level frame is selected
      selectedFrameContext.setSelectedFrameUUID(null)
    }
  }, [selectedElement, isInspectorActive, selectedFrameContext])

  const inspectorContextValue = useMemo((): InspectorContext => {
    return {
      selectedElement: selectedElement,
      isInspectorActive: isInspectorActive,
      sketchSceneRootElement: inspectorRootElement,
      setSelectedElement: setSelectedElement,
      setHoveredElement,
      inspectorStatus,
      hasAnyLayerWithExportableAssets,
      clickHandler,
      mouseMoveHandler,
      mouseDownHandle,
      isTreeFullyLoaded,
    }
  }, [
    selectedElement,
    isInspectorActive,
    inspectorRootElement,
    setSelectedElement,
    setHoveredElement,
    inspectorStatus,
    hasAnyLayerWithExportableAssets,
    clickHandler,
    mouseMoveHandler,
    mouseDownHandle,
    isTreeFullyLoaded,
  ])

  return (
    <inspectorContext.Provider value={inspectorContextValue}>
      <inspectorHoveredElementContext.Provider value={hoveredElement}>
        {isInspectorActive && selectedElement && (
          <InspectCommandBarRegistration />
        )}
        {children}
      </inspectorHoveredElementContext.Provider>
    </inspectorContext.Provider>
  )
}
